import localforage from 'localforage';
import { useEffect, useState } from 'react';
import {
  AppBar,
  Button,
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
} from 'ui/mui';
import { IconDarkMode, IconLightMode } from 'ui/Icons';
import { useDebug } from '../contexts/DebugContext';
import { activeElementTracker } from '../helpers/trackActiveElement';

const DebugToolbar = () => {
  const { setShowDebugToolbar, darkMode, toggleDarkMode } = useDebug();
  const [isTrackingActive, setIsTrackingActive] = useState(
    activeElementTracker.getStatus(),
  );
  const [isBlurSelectionActive, setIsBlurSelectionActive] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!isBlurSelectionActive) return;

    const handleClickToEdit = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Låt klick på toolbar-knappar gå igenom
      if (target.closest('[role="toolbar"], .MuiAppBar-root')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (target && target.tagName.toLowerCase() !== 'body') {
        const newText = prompt('Ny text:', target.textContent || '');
        if (newText !== null) {
          target.textContent = newText;
        }
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsBlurSelectionActive(false);
      }
    };

    document.addEventListener('click', handleClickToEdit, true);
    document.addEventListener('keydown', handleEscapeKey);
    document.body.style.cursor = 'crosshair';

    return () => {
      document.removeEventListener('click', handleClickToEdit, true);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.cursor = '';
    };
  }, [isBlurSelectionActive]);

  return (
    <AppBar
      position='fixed'
      sx={{
        backgroundColor: 'transparent',
      }}
    >
      <Toolbar
        variant='dense'
        sx={{
          gap: 2,
          minHeight: 48,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          color='inherit'
          size='small'
          onClick={async () => window.location.reload()}
        >
          ♻️Ladda om sidan
        </Button>
        <Button
          color='inherit'
          size='small'
          onClick={async () => await localforage.clear()}
        >
          🔥Rensa localforage
        </Button>
        <Button
          color='inherit'
          size='small'
          onClick={toggleDarkMode}
          startIcon={darkMode ? <IconLightMode /> : <IconDarkMode />}
        >
          {darkMode ? 'Ljust läge' : 'Mörkt läge'}
        </Button>
        <Button
          color='inherit'
          size='small'
          variant={isBlurSelectionActive ? 'contained' : 'text'}
          onClick={() => setIsBlurSelectionActive(!isBlurSelectionActive)}
        >
          {isBlurSelectionActive ? '✓ Klicka på element' : '✏️ Redigera text'}
        </Button>
        <Button
          id='basic-button'
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          Verktyg
        </Button>
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
          }}
        >
          <MenuItem
            onClick={() => {
              if (activeElementTracker.getStatus()) {
                activeElementTracker.stop();
                setIsTrackingActive(false);
              } else {
                activeElementTracker.start();
                setIsTrackingActive(true);
              }
            }}
          >
            <ListItemIcon>
              <Checkbox checked={isTrackingActive} sx={{ p: 0 }} />
            </ListItemIcon>
            <ListItemText>Spåra fokuserade element</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => setShowDebugToolbar(false)}>
            <ListItemIcon>💨</ListItemIcon>
            <ListItemText>Dölj toolbar</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DebugToolbar;
